import { ArrowLeft, ChevronDown, MoreHorizontal, Save, X } from "lucide-react";
import i18n from "../../../i18n";

function sq(key, options) {
  return i18n.t(`salesQuotation.${key}`, options);
}

function formatDisplayDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  const date = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(date.getTime())) return raw.slice(0, 10);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Compact meta chip for status / version / type */
function MobileMetaChip({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-[#eef2f8] text-[#586c8f]",
    status: "bg-[#eef2f8] text-[#2944a8]",
    version: "bg-[#f4f6fb] text-[#647b9f]",
    type: "bg-[#eef3ff] text-[#243c93]",
  };
  return (
    <span
      className={`inline-flex max-w-full items-center truncate rounded-full px-2 py-0.5 text-[11px] font-semibold leading-4 ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

/** Icon button for ⋯ */
function QuotationMobileMenuButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[#d8e0ed] bg-white text-[#51657f] transition active:bg-[#f7f9fc]"
      aria-label={sq("mobileMoreMenu")}
    >
      <MoreHorizontal className="h-4 w-4" />
    </button>
  );
}

/**
 * Mobile open-quotation header:
 * Row 1 — Back · Save (optional) · ⋯
 * Row 2 — Full Ref ID (wraps; never truncated)
 * Row 3 — Status / Version / Type chips
 */
export function QuotationMobileHeader({
  refId,
  onBack,
  onOpenMenu,
  status,
  versionLabel,
  typeLabel,
  showSave = false,
  saveBusy = false,
  onSave,
}) {
  const meta = [
    status ? { key: "status", label: status, tone: "status" } : null,
    versionLabel ? { key: "version", label: versionLabel, tone: "version" } : null,
    typeLabel ? { key: "type", label: typeLabel, tone: "type" } : null,
  ].filter(Boolean);

  return (
    <header className="space-y-1.5">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          className="page-back-link shrink-0 !min-h-9 !min-w-0 !px-2.5 !py-1.5 !text-[12px]"
          title={sq("back")}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <span>{sq("back")}</span>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {showSave ? (
            <button
              type="button"
              onClick={onSave}
              disabled={saveBusy}
              className="inline-flex h-9 items-center gap-1 rounded-lg border border-[#d8e0ed] bg-white px-2.5 text-[12px] font-semibold text-[#2944a8] transition active:bg-[#f7f9fc] disabled:cursor-not-allowed disabled:text-[#8da0be]"
            >
              <Save className="h-3.5 w-3.5" />
              <span>{saveBusy ? "…" : "Save"}</span>
            </button>
          ) : null}
          <QuotationMobileMenuButton onClick={onOpenMenu} />
        </div>
      </div>

      <h1 className="break-words text-[16px] font-semibold leading-5 tracking-[-0.02em] text-[#23324c] [overflow-wrap:anywhere]">
        {refId}
      </h1>

      {meta.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {meta.map((item) => (
            <MobileMetaChip key={item.key} tone={item.tone}>
              {item.label}
            </MobileMetaChip>
          ))}
        </div>
      ) : null}
    </header>
  );
}

/** Collapsible customer strip — Items tab only */
export function QuotationMobileCustomerCard({
  open,
  onToggle,
  quotation,
  editable,
  detailsSaving,
  canSave,
  onChangeField,
  onSave,
  isServiceQuotation,
  serviceExecutiveLabel,
  serviceManagerLabel,
}) {
  const tender = formatDisplayDate(quotation.tenderDue);
  const expiry = formatDisplayDate(quotation.expiry);

  return (
    <div className="overflow-hidden rounded-[10px] border border-[#e4eaf4] bg-[#fafbfe]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left"
      >
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-[#2d394c]">
            {[quotation.customer, quotation.location].filter(Boolean).join(" · ") || "Customer"}
          </p>
          {!open ? (
            <p className="mt-0.5 truncate text-[11px] text-[#8ea0bd]">
              {tender} → {expiry}
            </p>
          ) : null}
        </div>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-[#8ea0bd] transition ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="space-y-2.5 border-t border-[#eef1f7] px-3 py-2.5">
          <p className="text-[12px] leading-5 text-[#6f82a1]">
            {[quotation.pointOfContact, quotation.phone, quotation.email]
              .filter(Boolean)
              .join(" · ") || "No contact details"}
          </p>
          {isServiceQuotation ? (
            <p className="text-[11px] text-[#8ea0bd]">
              {sq("serviceExecutive")}: {serviceExecutiveLabel || "—"} · {sq("serviceManager")}:{" "}
              {serviceManagerLabel || "—"}
            </p>
          ) : (
            <p className="text-[11px] text-[#8ea0bd]">
              {sq("salesExecutive")}: {quotation.salesExecutive || "—"} · {sq("salesSupport")}:{" "}
              {quotation.salesSupport || "—"}
            </p>
          )}
          {editable && canSave ? (
            <div className="grid grid-cols-2 gap-2">
              <label className="block">
                <span className="mb-1 block text-[11px] text-[#8ea0bd]">{sq("tenderDueDate")}</span>
                <input
                  type="date"
                  value={String(quotation.tenderDue || "").slice(0, 10)}
                  onChange={(event) => onChangeField("tenderDue", event.target.value)}
                  className="h-9 w-full rounded-lg border border-[#d8e0ed] bg-white px-2 text-[13px] text-[#23324c]"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[11px] text-[#8ea0bd]">{sq("quotationExpiryDate")}</span>
                <input
                  type="date"
                  value={String(quotation.expiry || "").slice(0, 10)}
                  onChange={(event) => onChangeField("expiry", event.target.value)}
                  className="h-9 w-full rounded-lg border border-[#d8e0ed] bg-white px-2 text-[13px] text-[#23324c]"
                />
              </label>
              <button
                type="button"
                onClick={onSave}
                disabled={detailsSaving}
                className="col-span-2 inline-flex min-h-[40px] items-center justify-center rounded-[10px] bg-[#243c93] px-3 text-[13px] font-semibold text-white disabled:bg-[#a6b3d6]"
              >
                {detailsSaving ? "Saving..." : "Save details"}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Summary tab — all sheet totals + context for submit */
export function QuotationMobileSummaryPanel({
  formatMoney,
  sellingTotal,
  costTotal,
  systemsCount,
  itemsCount,
  supplyInputAmount,
  inputInstallationAmount,
  supplySubtotal,
  installationSubtotal,
  showInstallation,
  grossProfit,
  grossMarginPct,
}) {
  return (
    <div className="space-y-3">
      <div className="rounded-[12px] border border-[#e4eaf4] bg-white p-3.5">
        <p className="text-[11px] font-medium text-[#8ea0bd]">
          {sq("mobileSellingTotalMeta", { systems: systemsCount, items: itemsCount })}
        </p>
        <p className="mt-1 text-[24px] font-semibold tracking-[-0.03em] text-[#23324c]">
          {formatMoney(sellingTotal)}
        </p>
        <div className="mt-3 space-y-2 border-t border-[#eef1f7] pt-2.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-[#8ea0bd]">{sq("mobileCostLabel")}</span>
            <span className="text-[14px] font-semibold text-[#51657f]">{formatMoney(costTotal)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-[#8ea0bd]">{sq("mobileGrossProfit")}</span>
            <span className="text-[14px] font-semibold text-[#23324c]">{formatMoney(grossProfit)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[12px] text-[#8ea0bd]">{sq("mobileGrossMargin")}</span>
            <span className="text-[14px] font-semibold text-[#23324c]">
              {Number(grossMarginPct || 0).toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      <div className="rounded-[12px] border border-[#e4eaf4] bg-white p-3.5">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6f82a1]">
          {sq("grandTotal")}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <SummaryTotalCell label={sq("inputSupplyTotal")} value={formatMoney(supplyInputAmount)} />
          <SummaryTotalCell label={sq("sellingSupplyTotal")} value={formatMoney(supplySubtotal)} />
          {showInstallation ? (
            <>
              <SummaryTotalCell
                label={sq("inputInstallationTotal")}
                value={formatMoney(inputInstallationAmount)}
              />
              <SummaryTotalCell
                label={sq("sellingInstallationTotal")}
                value={formatMoney(installationSubtotal)}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function SummaryTotalCell({ label, value }) {
  return (
    <div className="rounded-[10px] bg-[#f7f9fc] px-3 py-2.5">
      <p className="text-[11px] leading-4 text-[#6f82a1]">{label}</p>
      <p className="mt-1 text-[15px] font-semibold text-[#23324c]">{value}</p>
    </div>
  );
}

export function QuotationMobileMenuSheet({
  open,
  onClose,
  showCreateNewVersion,
  createVersionDisabled,
  onCreateNewVersion,
  onVersionHistory,
  showDownload,
  onDownloadPdf,
  isServiceQuotation,
  complaintId,
  complaintBackBusy,
  onBackToComplaint,
  actionBusy,
}) {
  if (!open) return null;

  const items = [
    showCreateNewVersion
      ? {
          key: "version",
          label: sq("createNewVersion"),
          disabled: createVersionDisabled || actionBusy,
          onClick: onCreateNewVersion,
        }
      : null,
    {
      key: "history",
      label: sq("versionHistory"),
      disabled: actionBusy,
      onClick: onVersionHistory,
    },
    showDownload
      ? {
          key: "pdf",
          label: sq("downloadPdf"),
          disabled: actionBusy,
          onClick: onDownloadPdf,
        }
      : null,
    isServiceQuotation && complaintId
      ? {
          key: "complaint",
          label: sq("mobileGoToComplaint", { id: complaintId }),
          disabled: complaintBackBusy || actionBusy,
          onClick: onBackToComplaint,
        }
      : null,
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-black/40"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="max-h-[70vh] w-full overflow-y-auto rounded-t-[18px] bg-[#f4f6fb] px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2">
        <div className="mb-2 flex items-center justify-between px-1">
          <p className="text-[14px] font-semibold text-[#23324c]">{sq("mobileMoreMenu")}</p>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full text-[#51657f]"
            aria-label={sq("close")}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-2">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                onClose();
                item.onClick?.();
              }}
              className="block w-full rounded-[12px] border border-[#d8e0ed] bg-white px-4 py-3.5 text-left text-[14px] font-semibold text-[#23324c] disabled:cursor-not-allowed disabled:text-[#8da0be]"
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** In-flow footer: Back / Next on Items–Terms; Submit only on Summary (no sticky overlay). */
export function QuotationMobileFooterBar({
  activeTab,
  showSubmit,
  actionBusy,
  submittingLabel,
  submitLabel,
  onBackTab,
  onNextTab,
  onSubmit,
  waitingLabel,
}) {
  const isItems = activeTab === "workingSheet";
  const isPricing = activeTab === "costPricing";
  const isTerms = activeTab === "terms";
  const isSummary = activeTab === "summary";

  let primaryLabel = sq("mobileNextPricing");
  if (isPricing) primaryLabel = sq("mobileNextTerms");
  if (isTerms) primaryLabel = sq("mobileNextSummary");
  if (isSummary) primaryLabel = actionBusy ? submittingLabel : submitLabel;

  const primaryDisabled = isSummary ? !showSubmit || actionBusy : actionBusy;

  function handlePrimary() {
    if (isItems || isPricing || isTerms) {
      onNextTab?.();
      return;
    }
    if (showSubmit) onSubmit?.();
  }

  return (
    <div className="flex items-stretch gap-3">
      {!isItems ? (
        <button
          type="button"
          onClick={onBackTab}
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center rounded-[12px] border border-[#d0d9ea] bg-white px-4 text-[13px] font-semibold text-[#2944a8]"
        >
          {sq("back")}
        </button>
      ) : null}
      {isSummary && !showSubmit ? (
        <span className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-[12px] bg-[#eef2f8] px-4 text-[13px] font-semibold text-[#586c8f]">
          {waitingLabel}
        </span>
      ) : (
        <button
          type="button"
          onClick={handlePrimary}
          disabled={primaryDisabled}
          className="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-[12px] bg-[#243c93] px-5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#a6b3d6]"
        >
          {primaryLabel}
        </button>
      )}
    </div>
  );
}
